import "./style.css";
import { GoldenGroveGame, type GameState, type GameStats } from "./game/game";

function required<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`Golden Grove is missing the required ${selector} element.`);
  return element;
}

const canvas = required<HTMLCanvasElement>("#game");
const menu = required<HTMLElement>("#menu");
const menuTitle = required<HTMLElement>("#menuTitle");
const menuCopy = required<HTMLElement>("#menuCopy");
const menuStats = required<HTMLElement>("#menuStats");
const levelMap = required<HTMLElement>("#levelMap");
const worldTease = required<HTMLElement>("#worldTease");
const worldTeaseTitle = required<HTMLElement>("#worldTeaseTitle");
const worldTeaseCopy = required<HTMLElement>("#worldTeaseCopy");
const playButton = required<HTMLButtonElement>("#playButton");
const resultActions = required<HTMLElement>("#resultActions");
const nextLevelButton = required<HTMLButtonElement>("#nextLevelButton");
const retryLevelButton = required<HTMLButtonElement>("#retryLevelButton");
const worldMapButton = required<HTMLButtonElement>("#worldMapButton");
const pauseButton = required<HTMLButtonElement>("#pauseButton");
const soundButton = required<HTMLButtonElement>("#soundButton");
const settingsButton = required<HTMLButtonElement>("#settingsButton");
const toast = required<HTMLElement>("#toast");
const settingsPanel = required<HTMLElement>("#settingsPanel");
const closeSettingsButton = required<HTMLButtonElement>("#closeSettingsButton");
const doneSettingsButton = required<HTMLButtonElement>("#doneSettingsButton");
const musicVolume = required<HTMLInputElement>("#musicVolume");
const musicValue = required<HTMLOutputElement>("#musicValue");
const sfxVolume = required<HTMLInputElement>("#sfxVolume");
const sfxValue = required<HTMLOutputElement>("#sfxValue");
const reducedMotion = required<HTMLInputElement>("#reducedMotion");
const leftHandedTouch = required<HTMLInputElement>("#leftHandedTouch");
const touchSize = required<HTMLElement>("#touchSize") as unknown as HTMLSelectElement;
const resetProgressButton = required<HTMLButtonElement>("#resetProgressButton");

let toastTimer = 0;

function formatTime(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const remaining = Math.floor(seconds % 60);
  return `${minutes}:${remaining.toString().padStart(2, "0")}`;
}

function showToast(message: string): void {
  window.clearTimeout(toastTimer);
  toast.textContent = message;
  toast.classList.add("is-visible");
  toastTimer = window.setTimeout(() => toast.classList.remove("is-visible"), 2400);
}

let game: GoldenGroveGame;

function renderLevelMap(stats: GameStats): void {
  levelMap.replaceChildren();
  const worldNumbers = [...new Set(stats.levels.filter((level) => !level.bonus).map((level) => level.worldNumber))];
  for (const worldNumber of worldNumbers) {
    const worldLevels = stats.levels
      .map((level, index) => ({ level, index }))
      .filter(({ level }) => level.worldNumber === worldNumber);
    const section = document.createElement("section");
    section.className = `world-map-section world-${worldNumber} ${worldLevels.some(({ level }) => level.unlocked) ? "" : "is-world-locked"}`;

    const heading = document.createElement("div");
    heading.className = "world-map-heading";
    const title = document.createElement("h2");
    title.textContent = `World ${worldNumber} · ${worldLevels[0]?.level.worldName ?? "World"}`;
    const summary = document.createElement("span");
    const cleared = worldLevels.filter(({ level }) => level.completed).length;
    summary.textContent = worldLevels.some(({ level }) => level.unlocked)
      ? `${cleared}/${worldLevels.length} cleared`
      : "Complete Sunny Grove to unlock";
    heading.appendChild(title);
    heading.appendChild(summary);

    const grid = document.createElement("div");
    grid.className = "world-level-grid";
    for (const { level, index } of worldLevels) {
      const button = document.createElement("button");
      button.type = "button";
      button.className = `level-node ${level.completed ? "is-complete" : ""} ${level.unlocked ? "" : "is-locked"} ${index === stats.levelIndex ? "is-selected" : ""}`;
      button.disabled = !level.unlocked;
      button.setAttribute(
        "aria-label",
        level.unlocked ? `Play ${level.worldName} level ${level.worldLevel}: ${level.name}` : `${level.worldName} level ${level.worldLevel} locked`,
      );

      const number = document.createElement("span");
      number.className = "level-number";
      number.textContent = level.unlocked ? String(level.worldLevel) : "×";
      const details = document.createElement("span");
      details.className = "level-details";
      const levelTitle = document.createElement("strong");
      levelTitle.textContent = level.name;
      const progress = document.createElement("small");
      progress.textContent = level.completed
        ? `${level.grade} • ${level.bestPins}/3 Pins • ${level.crown ? "Crown found" : "Crown hidden"}`
        : level.unlocked
          ? "New trail"
          : "Complete the previous trail";
      details.appendChild(levelTitle);
      details.appendChild(progress);
      button.appendChild(number);
      button.appendChild(details);
      button.addEventListener("click", () => game.selectLevel(index));
      grid.appendChild(button);
    }
    section.appendChild(heading);
    section.appendChild(grid);
    levelMap.appendChild(section);
  }

  const bonusEntry = stats.levels
    .map((level, index) => ({ level, index }))
    .find(({ level }) => level.bonus);
  if (bonusEntry) {
    const { level, index } = bonusEntry;
    const section = document.createElement("section");
    section.className = `world-map-section crown-vault ${level.unlocked ? "" : "is-world-locked"}`;
    const heading = document.createElement("div");
    heading.className = "world-map-heading";
    const title = document.createElement("h2");
    title.textContent = "Crown Vault · Bonus Challenge";
    const summary = document.createElement("span");
    summary.textContent = level.unlocked ? "Vault open" : `${stats.crownCount}/5 Crown Pieces`;
    heading.appendChild(title);
    heading.appendChild(summary);

    const grid = document.createElement("div");
    grid.className = "world-level-grid bonus-grid";
    const button = document.createElement("button");
    button.type = "button";
    button.className = `level-node bonus-node ${level.completed ? "is-complete" : ""} ${level.unlocked ? "" : "is-locked"} ${index === stats.levelIndex ? "is-selected" : ""}`;
    button.disabled = !level.unlocked;
    button.setAttribute(
      "aria-label",
      level.unlocked ? `Play Crown Vault bonus challenge: ${level.name}` : "Crown Vault bonus challenge locked",
    );
    const number = document.createElement("span");
    number.className = "level-number";
    number.textContent = level.unlocked ? "♛" : "×";
    const details = document.createElement("span");
    details.className = "level-details";
    const levelTitle = document.createElement("strong");
    levelTitle.textContent = level.name;
    const progress = document.createElement("small");
    progress.textContent = level.completed
      ? `${level.grade} • ${level.bestPins}/3 Pins • ${level.bestTime ? `Best ${formatTime(level.bestTime)}` : "Cleared"}`
      : level.unlocked
        ? "Three powers · three Pins · one fast finish"
        : "Find five campaign Crown Pieces to unlock";
    details.appendChild(levelTitle);
    details.appendChild(progress);
    button.appendChild(number);
    button.appendChild(details);
    button.addEventListener("click", () => game.selectLevel(index));
    grid.appendChild(button);
    section.appendChild(heading);
    section.appendChild(grid);
    levelMap.appendChild(section);
  }
}

function setMenu(state: GameState, stats: GameStats): void {
  menu.dataset.state = state;
  resultActions.hidden = state !== "won";
  nextLevelButton.hidden = !stats.canAdvance;
  if (state === "playing") {
    menu.classList.add("is-hidden");
    pauseButton.disabled = false;
    return;
  }

  menu.classList.remove("is-hidden");
  pauseButton.disabled = state !== "paused";
  menuStats.hidden = state === "ready" || state === "map";
  levelMap.hidden = state !== "map";
  playButton.hidden = state === "map" || state === "won";
  worldTease.hidden = state !== "worldcomplete";

  if (state === "ready") {
    menuTitle.textContent = "Golden Grove";
    menuCopy.textContent =
      "Ten campaign trails, four exploration powers and a Crown Vault bonus challenge.";
    playButton.textContent = "Open world map";
  } else if (state === "map") {
    const campaignLevels = stats.levels.filter((level) => !level.bonus);
    const complete = campaignLevels.filter((level) => level.completed).length;
    menuTitle.textContent = "World Map";
    menuCopy.textContent = `${complete}/${stats.totalLevels} trails cleared • ${stats.crownCount}/${stats.totalLevels} Crown Pieces • ${
      stats.goldenHero ? "Golden Hero unlocked" : stats.bonusUnlocked ? "Crown Vault open" : `${5 - stats.crownCount} to the Crown Vault`
    }`;
    renderLevelMap(stats);
  } else if (state === "paused") {
    menuTitle.textContent = "Adventure paused";
    menuCopy.textContent = stats.bonus ? `Crown Vault · ${stats.levelName}` : `${stats.worldName} · Level ${stats.worldLevel}: ${stats.levelName}`;
    menuStats.textContent = `${stats.pins}/3 Golden Pins • ${stats.bonus ? `Par ${formatTime(stats.parTime)}` : stats.crown ? "Crown found" : "Crown hidden"} • ${formatTime(stats.time)}`;
    playButton.textContent = "Keep exploring";
  } else if (state === "won") {
    const perfect = stats.pins === 3 && (stats.bonus ? stats.time <= stats.parTime : stats.crown && stats.time <= stats.parTime);
    menuTitle.textContent = stats.bonus ? (perfect ? "Vault mastered!" : "Crown Rush cleared!") : perfect ? "Perfect level!" : "Trail cleared!";
    menuCopy.textContent = stats.bonus
      ? perfect
        ? "Three Golden Pins and a sub-par finish—the Crown Vault is sparkling."
        : "The vault is cleared. Replay it to catch every Pin before the par-time bell."
      : perfect
        ? `${stats.levelName} is complete with three Pins, its Crown Piece and a par-time finish.`
        : `You cleared ${stats.levelName}. Its power routes still hold a perfect result.`;
    menuStats.textContent = `Score ${stats.score} • ${stats.pins}/3 Pins • ${stats.bonus ? `Par ${formatTime(stats.parTime)}` : stats.crown ? "Crown found" : "Crown hidden"} • ${formatTime(stats.time)}${
      stats.bestTime ? ` • Best ${formatTime(stats.bestTime)}` : ""
    } • Perfect: 3 Pins + ${stats.bonus ? "under par" : "Crown + under par"}`;
    nextLevelButton.textContent = "Next level";
  } else if (state === "worldcomplete") {
    const currentWorld = stats.levels.filter((level) => !level.bonus && level.worldNumber === stats.worldNumber);
    const crowns = currentWorld.filter((level) => level.crown).length;
    const golds = currentWorld.filter((level) => level.grade === "Gold").length;
    if (stats.worldNumber === 1) {
      menuTitle.textContent = "Sunny Grove saved!";
      menuCopy.textContent = "The fruit cart is safe—and the sparkling coast beyond the gate is now open.";
      worldTeaseTitle.textContent = "World 2 unlocked: Soda Pop Shore";
      worldTeaseCopy.textContent = "Bottle caps, fizz geysers and a runaway soda float are waiting by the sea.";
    } else {
      menuTitle.textContent = "Soda Pop Shore saved!";
      menuCopy.textContent = "The soda float reached the lighthouse and both worlds are open for replay and secret hunting.";
      worldTeaseTitle.textContent = "Two worlds complete!";
      worldTeaseCopy.textContent = "Every Sunny Grove and Soda Pop Shore trail is now open.";
    }
    menuStats.textContent = `${crowns}/${currentWorld.length} Crown Pieces • ${golds}/${currentWorld.length} Gold clears • Final score ${stats.score}`;
    playButton.textContent = "Return to the map";
  } else if (state === "gameover") {
    menuTitle.textContent = "One more bounce?";
    menuCopy.textContent = stats.checkpoint
      ? `The checkpoint in ${stats.levelName} is behind you—you know the trickiest half already.`
      : `Every jump teaches you something. Give ${stats.levelName} another go!`;
    menuStats.textContent = `${stats.pins}/3 Golden Pins • Score ${stats.score} • ${formatTime(stats.time)}`;
    playButton.textContent = "Retry level";
  }
}

game = new GoldenGroveGame(canvas, {
  onStateChange: setMenu,
  onToast: showToast,
  onDebugChange: (enabled) => {
    document.body.dataset.debug = String(enabled);
  },
});

function updateSoundButton(): void {
  soundButton.textContent = game.muted ? "×" : "♪";
  soundButton.setAttribute("aria-label", game.muted ? "Turn sound on" : "Mute sound");
  soundButton.title = game.muted ? "Sound off" : "Sound on";
}

function storedBoolean(key: string, fallback = false): boolean {
  const value = localStorage.getItem(key);
  return value === null ? fallback : value === "true";
}

function applyInterfaceSettings(): void {
  document.body.dataset.reducedMotion = String(reducedMotion.checked);
  document.body.dataset.leftHanded = String(leftHandedTouch.checked);
  document.body.dataset.touchSize = touchSize.value;
  document.body.dataset.debug = String(game.debugEnabled);
  game.setReducedMotion(reducedMotion.checked);
}

function openSettings(): void {
  game.pause();
  settingsPanel.hidden = false;
  closeSettingsButton.focus();
}

function closeSettings(): void {
  settingsPanel.hidden = true;
  settingsButton.focus();
}

playButton.addEventListener("click", () => game.start());
nextLevelButton.addEventListener("click", () => game.start());
retryLevelButton.addEventListener("click", () => game.retryLevel());
worldMapButton.addEventListener("click", () => game.openMap());
pauseButton.addEventListener("click", () => game.togglePause());
soundButton.addEventListener("click", () => {
  game.audio.toggle();
  updateSoundButton();
});

settingsButton.addEventListener("click", openSettings);
closeSettingsButton.addEventListener("click", closeSettings);
doneSettingsButton.addEventListener("click", closeSettings);
settingsPanel.addEventListener("pointerdown", (event) => {
  if (event.target === settingsPanel) closeSettings();
});

musicVolume.value = String(Math.round(game.audio.musicLevel * 100));
musicValue.value = `${musicVolume.value}%`;
sfxVolume.value = String(Math.round(game.audio.sfxLevel * 100));
sfxValue.value = `${sfxVolume.value}%`;
reducedMotion.checked = storedBoolean(
  "uppg-reduced-motion",
  window.matchMedia("(prefers-reduced-motion: reduce)").matches,
);
leftHandedTouch.checked = storedBoolean("uppg-left-handed");
touchSize.value = localStorage.getItem("uppg-touch-size") ?? "comfortable";

musicVolume.addEventListener("input", () => {
  game.audio.setMusicVolume(Number(musicVolume.value) / 100);
  musicValue.value = `${musicVolume.value}%`;
});
sfxVolume.addEventListener("input", () => {
  game.audio.setSfxVolume(Number(sfxVolume.value) / 100);
  sfxValue.value = `${sfxVolume.value}%`;
});
reducedMotion.addEventListener("change", () => {
  localStorage.setItem("uppg-reduced-motion", String(reducedMotion.checked));
  applyInterfaceSettings();
});
leftHandedTouch.addEventListener("change", () => {
  localStorage.setItem("uppg-left-handed", String(leftHandedTouch.checked));
  applyInterfaceSettings();
});
touchSize.addEventListener("change", () => {
  localStorage.setItem("uppg-touch-size", touchSize.value);
  applyInterfaceSettings();
});
resetProgressButton.addEventListener("click", () => {
  if (!window.confirm("Reset every level result, Crown Piece and best time on this device?")) return;
  game.resetProgress();
  closeSettings();
});
window.addEventListener("keydown", (event) => {
  if (!settingsPanel.hidden && event.code === "Escape") {
    event.preventDefault();
    event.stopImmediatePropagation();
    closeSettings();
  }
}, { capture: true });

updateSoundButton();
applyInterfaceSettings();
